import React, { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAllParameters,
  refreshRemoteConfig,
  updateRemoteConfigParams,
  validateParameterKey,
} from "@/services/firebase/remoteConfig";
import { RemoteConfigParameter, RemoteConfigValueType } from "@/types/remoteConfig";
import { usePermissions } from "@/hooks/usePermissions";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import {
  AdminPage,
  AdminPageHeader,
  AdminLoadingSkeleton,
  AdminErrorAlert,
} from "@/components/admin-ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Plus, Save, X, Trash2 } from "lucide-react";

export default function SettingsPage() {
  const { canManageSettings } = usePermissions();
  const queryClient = useQueryClient();
  const [editingParams, setEditingParams] = useState<Record<string, string | number | boolean>>({});
  const [newParamKey, setNewParamKey] = useState("");
  const [newParamValue, setNewParamValue] = useState("");
  const [newParamType, setNewParamType] = useState<RemoteConfigValueType>("string");
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    key: string | null;
  }>({ isOpen: false, key: null });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // 使用 useRef 來追蹤 timeout，以便清理
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 清理 timeout
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  // 獲取所有參數
  const { data: parameters, isLoading, error: fetchError } = useQuery({
    queryKey: ["remoteConfig"],
    queryFn: () => getAllParameters(),
    enabled: canManageSettings,
  });

  // 重新載入 mutation
  const refreshMutation = useMutation({
    mutationFn: () => refreshRemoteConfig(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["remoteConfig"] });
      setSuccess("Remote Config 已重新載入");
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => setSuccess(null), 3000);
    },
    onError: (error) => {
      setError(error instanceof Error ? error.message : "重新載入失敗");
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(() => setError(null), 5000);
    },
  });

  // 更新參數 mutation
  const updateMutation = useMutation({
    mutationFn: ({
      params,
      deleteKeys,
    }: {
      params: Record<string, string | number | boolean>;
      deleteKeys?: string[];
    }) => updateRemoteConfigParams(params, deleteKeys),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["remoteConfig"] });
      setEditingParams({});
      setSuccess("參數已更新並立即生效");
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => setSuccess(null), 3000);
    },
    onError: (error) => {
      setError(error instanceof Error ? error.message : "更新失敗");
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(() => setError(null), 5000);
    },
  });

  // 處理參數值編輯
  const handleParamChange = (key: string, value: string | number | boolean) => {
    setEditingParams((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // 處理新增參數
  const handleAddParam = () => {
    const trimmedKey = newParamKey.trim();

    if (!trimmedKey) {
      setError("參數鍵不能為空");
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(() => setError(null), 3000);
      return;
    }

    // 驗證參數鍵格式
    const keyValidation = validateParameterKey(trimmedKey);
    if (!keyValidation.valid) {
      setError(keyValidation.error || "參數鍵格式無效");
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(() => setError(null), 3000);
      return;
    }

    // 檢查參數鍵是否已存在
    if (parameters?.some((p) => p.key === trimmedKey)) {
      setError(`參數鍵 "${trimmedKey}" 已存在`);
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(() => setError(null), 3000);
      return;
    }

    // 檢查是否在編輯狀態中
    if (Object.prototype.hasOwnProperty.call(editingParams, trimmedKey)) {
      setError(`參數鍵 "${trimmedKey}" 已在編輯中`);
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(() => setError(null), 3000);
      return;
    }

    let convertedValue: string | number | boolean = newParamValue;
    if (newParamType === "boolean") {
      if (newParamValue !== "true" && newParamValue !== "false") {
        setError("布林值必須是 'true' 或 'false'");
        if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = setTimeout(() => setError(null), 3000);
        return;
      }
      convertedValue = newParamValue === "true";
    } else if (newParamType === "number") {
      if (!newParamValue.trim()) {
        setError("數字值不能為空");
        if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = setTimeout(() => setError(null), 3000);
        return;
      }
      convertedValue = Number(newParamValue);
      if (isNaN(convertedValue) || !isFinite(convertedValue)) {
        setError("無效的數字值");
        if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = setTimeout(() => setError(null), 3000);
        return;
      }
    }

    handleParamChange(trimmedKey, convertedValue);
    setNewParamKey("");
    setNewParamValue("");
    setNewParamType("string");
  };

  // 處理刪除參數
  const handleDeleteParam = (key: string) => {
    setDeleteConfirm({ isOpen: true, key });
  };

  const confirmDelete = () => {
    if (deleteConfirm.key) {
      // 將參數值設為空字串以標記刪除（實際刪除需要在 Functions 中處理）
      handleParamChange(deleteConfirm.key, "");
      setDeleteConfirm({ isOpen: false, key: null });
    }
  };

  // 處理保存變更
  const handleSaveChanges = () => {
    if (Object.keys(editingParams).length === 0) {
      setError("沒有變更需要保存");
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(() => setError(null), 3000);
      return;
    }

    // 分離更新和刪除的參數
    const paramsToUpdate: Record<string, string | number | boolean> = {};
    const keysToDelete: string[] = [];

    for (const [key, value] of Object.entries(editingParams)) {
      if (value === "" || value === null || value === undefined) {
        keysToDelete.push(key);
      } else {
        // 驗證參數鍵格式
        const keyValidation = validateParameterKey(key);
        if (!keyValidation.valid) {
          setError(`參數鍵 "${key}" 格式無效: ${keyValidation.error}`);
          if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
          errorTimeoutRef.current = setTimeout(() => setError(null), 5000);
          return;
        }
        paramsToUpdate[key] = value;
      }
    }

    if (Object.keys(paramsToUpdate).length === 0 && keysToDelete.length === 0) {
      setError("沒有有效的變更需要保存");
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(() => setError(null), 3000);
      return;
    }

    updateMutation.mutate({
      params: paramsToUpdate,
      deleteKeys: keysToDelete.length > 0 ? keysToDelete : undefined,
    });
  };

  // 獲取參數的顯示值
  const getParamDisplayValue = (param: RemoteConfigParameter): string | number | boolean => {
    if (Object.prototype.hasOwnProperty.call(editingParams, param.key)) {
      return editingParams[param.key];
    }
    return param.value;
  };

  // 檢查是否有未保存的變更
  const hasUnsavedChanges = Object.keys(editingParams).length > 0;

  if (!canManageSettings) {
    return (
      <AdminPage>
        <AdminErrorAlert message="您沒有權限訪問系統設定" />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader
        title="Firebase Remote Config 管理"
        subtitle="管理應用程式的遠端配置參數，無需重新發布應用程式"
        onRefresh={() => refreshMutation.mutate()}
        loading={refreshMutation.isPending}
        actions={
          hasUnsavedChanges ? (
            <Button
              onClick={handleSaveChanges}
              disabled={updateMutation.isPending}
            >
              <Save className="h-4 w-4" />
              {updateMutation.isPending ? "發布中..." : "保存並發布"}
            </Button>
          ) : undefined
        }
      />

      {/* 錯誤訊息 */}
      {error && (
        <AdminErrorAlert message={error} onDismiss={() => setError(null)} />
      )}

      {/* 成功訊息 */}
      {success && (
        <Alert className="mb-4 border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20">
          <AlertDescription className="flex items-center justify-between">
            <span className="text-emerald-700 dark:text-emerald-400">{success}</span>
            <button
              onClick={() => setSuccess(null)}
              className="ml-2 text-emerald-600 hover:opacity-70"
            >
              <X className="h-4 w-4" />
            </button>
          </AlertDescription>
        </Alert>
      )}

      {/* 未保存變更提示 */}
      {hasUnsavedChanges && (
        <Alert className="mb-4 border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20">
          <AlertDescription className="text-amber-700 dark:text-amber-400">
            您有未發布的變更，請按「保存並發布」使設定立即生效
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <AdminLoadingSkeleton rows={5} variant="table" />
      ) : fetchError ? (
        <AdminErrorAlert
          message={`載入 Remote Config 時發生錯誤：${fetchError instanceof Error ? fetchError.message : "未知錯誤"}`}
        />
      ) : (
        <div className="space-y-6">
          {/* 新增參數區塊 */}
          <Card>
            <CardHeader>
              <CardTitle>新增參數</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Input
                  type="text"
                  placeholder="參數鍵（例如：facebook_login_enabled）"
                  value={newParamKey}
                  onChange={(e) => setNewParamKey(e.target.value)}
                />
                <Select
                  value={newParamType}
                  onValueChange={(val) => setNewParamType(val as RemoteConfigValueType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="string">字串</SelectItem>
                    <SelectItem value="number">數字</SelectItem>
                    <SelectItem value="boolean">布林值</SelectItem>
                  </SelectContent>
                </Select>
                {newParamType === "boolean" ? (
                  <Select
                    value={newParamValue}
                    onValueChange={(val) => setNewParamValue(val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="選擇值" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">true</SelectItem>
                      <SelectItem value="false">false</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={newParamType === "number" ? "number" : "text"}
                    placeholder="參數值"
                    value={newParamValue}
                    onChange={(e) => setNewParamValue(e.target.value)}
                  />
                )}
                <Button onClick={handleAddParam}>
                  <Plus className="h-4 w-4" />
                  新增
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* 參數列表 */}
          <Card>
            <CardHeader>
              <CardTitle>現有參數 ({parameters?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {!parameters || parameters.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  目前沒有 Remote Config 參數
                </div>
              ) : (
                <div className="divide-y">
                  {parameters.map((param) => {
                    const displayValue = getParamDisplayValue(param);
                    const isEditing = Object.prototype.hasOwnProperty.call(editingParams, param.key);
                    const isDeleted = isEditing && editingParams[param.key] === "";

                    return (
                      <div
                        key={param.key}
                        className={`p-6 ${isDeleted ? "opacity-50 bg-destructive/5" : ""}`}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                          <div className="md:col-span-3">
                            <Label className="mb-1 block">參數鍵</Label>
                            <code className="text-sm text-foreground bg-muted px-2 py-1 rounded">
                              {param.key}
                            </code>
                          </div>
                          <div className="md:col-span-2">
                            <Label className="mb-1 block">類型</Label>
                            <Badge variant="secondary">
                              {param.valueType === "boolean"
                                ? "布林值"
                                : param.valueType === "number"
                                ? "數字"
                                : "字串"}
                            </Badge>
                          </div>
                          <div className="md:col-span-5">
                            <Label className="mb-1 block">值</Label>
                            {param.valueType === "boolean" ? (
                              <Select
                                value={String(displayValue)}
                                onValueChange={(val) =>
                                  handleParamChange(param.key, val === "true")
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="true">true</SelectItem>
                                  <SelectItem value="false">false</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : param.valueType === "number" ? (
                              <Input
                                type="number"
                                value={String(displayValue)}
                                onChange={(e) =>
                                  handleParamChange(param.key, Number(e.target.value))
                                }
                              />
                            ) : (
                              <Input
                                type="text"
                                value={String(displayValue)}
                                onChange={(e) =>
                                  handleParamChange(param.key, e.target.value)
                                }
                              />
                            )}
                          </div>
                          <div className="md:col-span-2 flex gap-2">
                            {isEditing && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const newParams = { ...editingParams };
                                  delete newParams[param.key];
                                  setEditingParams(newParams);
                                }}
                              >
                                取消
                              </Button>
                            )}
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteParam(param.key)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              刪除
                            </Button>
                          </div>
                        </div>
                        {isDeleted && (
                          <p className="mt-2 text-sm text-destructive">
                            此參數將被刪除
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* 刪除確認對話框 */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="確認刪除"
        message={`您確定要刪除參數 "${deleteConfirm.key}" 嗎？此操作無法復原。`}
        confirmText="刪除"
        cancelText="取消"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, key: null })}
      />

    </AdminPage>
  );
}
